
import { apiConfig } from '@/lib/env';

export interface HealthCheckResult {
  isHealthy: boolean;
  responseTime: number;
  error?: string;
  timestamp: Date;
  systemMetrics?: {
    cpu?: number;
    memory?: number;
    disk?: number;
    uptime?: string;
    servicesCount?: number;
    websocketConnections?: number;
    requestsProcessed?: number;
    system?: {
      cpu_usage?: string;
      memory_usage?: string;
      disk_usage?: string;
      process_memory?: string;
      uptime?: string;
    };
    services?: {
      database?: string;
      redis?: string;
      llm?: {
        groq?: {
          status?: string;
          details?: {
            url?: string;
          };
        };
        gemini?: {
          status?: string;
          details?: {
            url?: string;
          };
        };
        openai?: {
          status?: string;
          details?: {
            url?: string;
          };
        };
      };
    };
    version?: string;
  };
}

// In-flight deduplication for health checks
let inflightHealth: Promise<HealthCheckResult> | null = null;

export const checkBackendHealth = async (): Promise<HealthCheckResult> => {
  if (inflightHealth) return inflightHealth;

  inflightHealth = _doHealthCheck().finally(() => { inflightHealth = null; });
  return inflightHealth;
};

const _doHealthCheck = async (): Promise<HealthCheckResult> => {
  const startTime = Date.now();
  const timestamp = new Date();
  
  try {
    // Use direct fetch to public health endpoint (no authentication required)
    const response = await fetch(apiConfig.publicHealthUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Health check failed with status: ${response.status}`);
    }
    
    const data = await response.json();
    const responseTime = Date.now() - startTime;
    
    // Parse the API response structure
    const systemMetrics = {
      system: {
        cpu_usage: data.system?.cpu_usage,
        memory_usage: data.system?.memory_usage,
        disk_usage: data.system?.disk_usage,
        process_memory: data.system?.process_memory,
      },
      services: {
        database: data.services?.database,
        redis: data.services?.redis,
        llm: data.services?.llm,
      },
      version: data.version,
      timestamp: data.timestamp,
    };
    
    return {
      isHealthy: true,
      responseTime,
      timestamp,
      systemMetrics,
    };
  } catch (error: any) {
    const responseTime = Date.now() - startTime;
    console.error('❌ Backend health check failed:', error);
    
    return {
      isHealthy: false,
      responseTime,
      error: error.name === 'AbortError' ? 'Request timeout' : error.message,
      timestamp,
    };
  }
};

export const isNetworkError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = String(error.message || '').toLowerCase();
  return (
    error.name === 'TypeError' ||
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('cors') ||
    errorMessage.includes('connection')
  );
};
